import { DependencyList, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useMountEffect } from 'primereact/hooks';

import { IResponse } from 'app/models';

export const useAsyncState = <S>(callback: (value: S, prev: S) => Promise<S>, initial?: S | (() => S)): [S, Dispatch<SetStateAction<S>>] => {
  const [state, setState] = useState<S>();
  const callState = useCallback(async (value: SetStateAction<S>): Promise<void> => {
    if (value instanceof Function) value = value(state!);
    if (value === state) return;

    return callback(value, state!).then(setState);
  }, [callback, state]);

  useMountEffect(() => {
    if (initial) callState(initial).then();
  });

  return useMemo(() => [state!, callState], [state, callState]);
};

export const useAsyncMemo = <T>(factory: () => Promise<T>, deps: DependencyList): T => {
  const [state, setState] = useState<T>();
  useEffect(() => {
    factory().then(setState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state!;
};

export interface ILoad<T = any, P = any> {
  success: boolean;
  total: number;
  params?: P;
  data: T[];
  errors: string[];
}

export interface ILoader<T = any, P = any> extends ILoad<T, P> {
  loading: boolean;
  reload: (params?: P) => Promise<void>;
}

export const createLoader = <T = any, P = any>(loader: (params: P) => Promise<IResponse<T[]>> | void): (params: P, state?: ILoad<T, P>) => Promise<ILoad<T>> => {
  return (params: P, state?: ILoad<T, P>) => (loader(params) ?? Promise.reject())
    .catch(e => ({ success: false, errors: (e && [{ code: 422, message: e?.message }]) } as IResponse<T[]>))
    .then(({ success, data, errors, total }) => ({ ...state, params,
      success: !!success,
      data: (data ?? state?.data),
      errors: (errors ?? state?.errors),
      total: (total ?? state?.total),
    } as ILoad<T>));
};

export const useLoader = <T = any, P = any>(loader: (params: P) => Promise<IResponse<T[]>> | void, initial?: T[]): ILoader<T, P> => {
  const [state, setState] = useState<Partial<ILoader<T, P>>>({ loading: false, success: false, total: 0, params: {} as P,
    data: (initial ?? []),
    errors: [],
  });

  return useMemo(() => ({ ...state,
    reload: async (params: P) => {
      setState({ ...state, params, errors: [], loading: true });

      return (loader(params) ?? Promise.reject())
        .catch(e => ({ success: false, errors: (e ? [e?.message] : []) }))
        .then(({ success, data, errors, total }: any) => setState({ ...state, params,
          success: !!success,
          data: (data ?? state.data),
          errors: (errors ?? state.errors),
          total: (total ?? state.total),

          loading: false,
        }));
    },
  } as ILoader<T, P>), [loader, state, setState]);
};
